import React, { useState } from 'react'
import { Stack, Background, LobbyButton, StyledInput } from './styles'
import { Room, nullRoom, HistoryState, RoomAndPlayerUsername } from '../Types'
import { CreatePlayer, getRoom, RemovePlayer } from './utils'
import { useHistory } from 'react-router-dom'
import isObjEqual from 'fast-deep-equal'

import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useHistoryState } from '../StateContext'
import LoadingIndicator from '../components/LoadingIndicator'

let stopAlert = false

type doesRoomExistType = {
    status: number,
    room: Room
}

async function onSubmit(state:HistoryState, newGameId:any) {
    const username = state.username
    const oldGameId = state.gameId
    // Remove user from old room
    if (oldGameId !== "") {
        RemovePlayer(state.username, oldGameId)
    }
    let room = await getRoom(newGameId)
    if (room === nullRoom) {
        // Need to signal to user that the gameId was invalid
        alert(`You've entered an invalid gameId. Perhaps check your spelling? 
            ${newGameId} is what you entered`)
        stopAlert = true
        return ""
    }
    if (room.hasStarted === true) {
        alert("The game seems to have already started. You'll just have to wait or join another game.")
        return ""
    }
    return CreatePlayer(username, newGameId).then((obj:RoomAndPlayerUsername) => {
        return obj.username
    })
}

async function checkRoom(gameId:string):Promise<doesRoomExistType> {
    return getRoom(gameId).then(room => {
        if (!isObjEqual(room, nullRoom)) {
            return { status: 1, room:room }
        }
        return { status: -1, room:nullRoom }
    })
}

const Join = () => {
    let history = useHistory()

    const {state, setState} = useHistoryState()

    const [username, setUsername] = useState<string>(state.username)
    const [gameId, setGameId] = useState<string>(state.gameId)
    const [room, setRoom] = useState<Room>(nullRoom);

    // will be 0 if unchecked, 1 if checked and room exists, -1 if checked and room doesn't exist
    const [previousGameExists, setPreviousGameExists] = useState<number>(0)

    if (state.gameId !== "" && previousGameExists === 0) {
        checkRoom(state.gameId).then(doesRoomExist => {
            let status = doesRoomExist.status
            setPreviousGameExists(status)
            if (status === -1) {
                setState({username: state.username, gameId: ""})
            } else {
                setRoom(doesRoomExist.room)
            }
        })
        return <LoadingIndicator message="Checking previous game" />
    }

    if (previousGameExists === 1) {
        return (
            <Background>
                <Stack style={{ paddingTop: "60px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
                    <h1 style={{
                        fontSize: "48px",
                        color: "white",
                        marginBottom: "40px",
                        fontWeight: 700,
                        textShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                    }}>
                        Rejoin Game?
                    </h1>
                    <div style={{
                        color: "white",
                        fontSize: "18px",
                        maxWidth: "600px",
                        margin: "0 auto 40px auto",
                        lineHeight: "1.6",
                        padding: "0 20px"
                    }}>
                        Do you want to join a new game or try to join the previous lobby/game you were in?
                        You can only rejoin if your name is the same as before.
                        <br /><br />
                        If you were kicked before and want to try and join again, select "Join New Game".
                    </div>
                    <LobbyButton onClick={()=>{
                        RemovePlayer(state.username, state.gameId).then(() => {
                            setGameId("")
                            setPreviousGameExists(-1)
                            setState({username: state.username, gameId: ""})
                        })
                    }}>
                        Join New Game
                    </LobbyButton>
                    <LobbyButton onClick={()=>{
                        stopAlert = false
                        setState({username: state.username, gameId: state.gameId})
                        if (room.hasStarted) {
                            history.push(`/start/${gameId}`)
                        } else {
                            history.push(`/lobby/${gameId}`)
                        }
                    }}>
                        Rejoin Previous Game
                    </LobbyButton>
                </Stack>
            </Background>
        )
    } else {
        return (
            <Background>
                <ToastContainer />
                <Stack style={{ paddingTop: "60px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
                    <h1 style={{
                        fontSize: "48px",
                        color: "white",
                        marginBottom: "40px",
                        fontWeight: 700,
                        textShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                    }}>
                        Join a Game
                    </h1>
                    <StyledInput
                        type="text"
                        placeholder="Enter a Username"
                        value={username}
                        onChange={(t) => {
                            setUsername(t.target.value)
                            setState({username: t.target.value, gameId: state.gameId})
                        }}
                    />
                    <StyledInput
                        id="gameCodeInput"
                        type="text"
                        placeholder="Enter the game code"
                        onChange={(t) => { setGameId(t.target.value) }}
                    />
                    <LobbyButton onClick={() => {
                        if (username.length > 19) {
                            toast.error("Please enter a shorter name.")
                        } else {
                            onSubmit(state, gameId).then((uniqueUsername) => {
                                if (uniqueUsername !== "") {
                                    setState({username: uniqueUsername, gameId: gameId})
                                    history.replace(`/lobby/${gameId}`)
                                }
                            })
                        }
                    }} disabled={username === "" || gameId === ""}>
                        Submit
                    </LobbyButton>
                </Stack>
            </Background>
        )
    } 
}

export default Join